"use client"
// components/ClientParticles.jsx

import { useRef, useEffect } from "react"
import { usePathname } from "next/navigation"
import * as THREE from "three"
import { useScreenType } from "@/contexts/ScreenTypeContext"

export default function ClientParticles() {
  const mountRef = useRef<HTMLDivElement | null>(null)
  const pathname = usePathname()
  const {isMobile} = useScreenType()


  useEffect(() => {
    const mount = mountRef.current
    if (!mount) return

    mount.innerHTML = ""

    // Constants
    const particleCount = isMobile ? 25 : 42
    const particleSize = 12
    const spacing = particleSize * 12
    const gravity = new THREE.Vector3(0, -9.8, 0) // Stronger gravity pulling down
    let deltaTime = 1 / 60
    let mousePrevX = 0
    let mousePrevY = 0
    let mouseX = 0
    let mouseY = 0
    let mouseIsPressed = false

    // Initialize Three.js components
    const scene = new THREE.Scene()

    const camera = new THREE.OrthographicCamera(window.innerWidth / -2, window.innerWidth / 2, window.innerHeight / 2, window.innerHeight / -2, 0.1, 1000)
    camera.position.z = 100

    const renderer = new THREE.WebGLRenderer({ alpha: true })
    renderer.setClearColor(0x000000, 0)
    renderer.setSize(window.innerWidth, window.innerHeight)
    mount.appendChild(renderer.domElement)

    // Cloud SVG path
    const createCloudShape = () => {
      const shape = new THREE.Shape()

      // Main cloud path based on the SVG
      shape.moveTo(46.9999, 20.9481)
      shape.bezierCurveTo(46.9999, 16.6224, 43.573, 13.0831, 39.3034, 12.8865)
      shape.bezierCurveTo(39.5843, 12.1281, 39.7248, 11.3135, 39.7248, 10.4428)
      shape.bezierCurveTo(39.7248, 6.42599, 36.4664, 3.16763, 32.4497, 3.16763)
      shape.bezierCurveTo(28.4329, 3.16763, 28.8542, 3.89796, 27.5902, 5.04962)
      shape.bezierCurveTo(25.5116, 2.43731, 22.3094, 0.751953, 18.714, 0.751953)
      shape.bezierCurveTo(12.4501, 0.751953, 7.39403, 5.83612, 7.39403, 12.0719)
      shape.bezierCurveTo(7.39403, 18.3078, 7.39403, 12.6337, 7.42212, 12.9146)
      shape.bezierCurveTo(3.32109, 13.3079, 0.0908203, 16.7628, 0.0908203, 20.9481)
      shape.bezierCurveTo(0.0908203, 25.1334, 3.26491, 28.5603, 7.30976, 28.9817)
      shape.bezierCurveTo(7.87155, 33.2232, 11.4951, 36.5096, 15.9051, 36.5096)
      shape.bezierCurveTo(20.3151, 36.5096, 18.8264, 36.1163, 20.0623, 35.4422)
      shape.bezierCurveTo(21.6072, 37.1837, 23.8543, 38.3073, 26.3543, 38.3073)
      shape.bezierCurveTo(28.8542, 38.3073, 32.1126, 36.6501, 33.5732, 34.1501)
      shape.bezierCurveTo(34.135, 34.3186, 34.6968, 34.4029, 35.3148, 34.4029)
      shape.bezierCurveTo(38.545, 34.4029, 41.1573, 31.8468, 41.2697, 28.6727)
      shape.bezierCurveTo(44.5561, 27.6615, 46.9718, 24.5717, 46.9718, 20.9481)
      shape.lineTo(46.9999, 20.9481)

      return shape
    }

    // Particle class
    class Particle {
      pos: THREE.Vector3
      vel: THREE.Vector3
      acc: THREE.Vector3
      lastPos: THREE.Vector3
      densityFactor: number
      rotation: number
      rotationVel: number
      mesh: THREE.Mesh
      scale: number

      constructor(x: number, y: number) {
        this.pos = new THREE.Vector3(x, y, 0)
        // Initial velocity - lower vertical velocity to let particles fall down
        this.vel = new THREE.Vector3(Math.random() * 10 - 5, Math.random() * 2 - 1, 0)
        this.acc = new THREE.Vector3(0, 9.8, 0)
        this.lastPos = new THREE.Vector3(x, y, 0)
        this.densityFactor = 0
        this.rotation = Math.random() * Math.PI * 0.25
        this.rotationVel = Math.random() * 0.2 - 0.1

        // Random scale for variety
        this.scale = 0.65 + Math.random() * 1

        // Ajouter une opacité aléatoire entre 0.5 et 0.95
        const opacity = 0.5 + Math.random() * 0.95

        // Create cloud mesh
        const cloudShape = createCloudShape()
        const geometry = new THREE.ShapeGeometry(cloudShape)
        const material = new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: opacity
        })
        this.mesh = new THREE.Mesh(geometry, material)

        // Center and scale the cloud
        this.mesh.position.copy(this.pos)
        this.mesh.scale.set(this.scale, this.scale, this.scale)
        scene.add(this.mesh)
      }

      update() {
        // Store previous position
        this.lastPos.copy(this.pos)

        // Update rotation
        this.rotation += this.rotationVel * deltaTime

        // Apply gravity with density-based scaling
        const gravityScale = THREE.MathUtils.mapLinear(this.densityFactor, 0, 5, 1, 0.7)
        const gravityForce = gravity.clone().multiplyScalar(gravityScale)
        this.acc.add(gravityForce)

        // Mouse interaction
        if (mouseIsPressed) {
          const d = this.pos.distanceTo(new THREE.Vector3(mouseX, mouseY, 0))
          const maxDist = 250

          if (d < maxDist) {
            const mouseVel = new THREE.Vector3(mouseX - mousePrevX, mouseY - mousePrevY, 0)
            const densityScale = THREE.MathUtils.mapLinear(this.densityFactor, 0, 5, 1, 0.85)
            const force = mouseVel.clone().multiplyScalar(10 * densityScale)
            const strength = Math.pow(THREE.MathUtils.mapLinear(d, 0, maxDist, 1, 0), 1.75)
            force.multiplyScalar(strength)
            this.acc.add(force)
            this.rotationVel += mouseVel.length() * 0.01 * (Math.random() * 2 - 1)
          }
        }

        // Apply acceleration and damping
        const dampingFactor = THREE.MathUtils.mapLinear(this.densityFactor, 0, 5, 1, 1)
        this.vel.add(this.acc.clone().multiplyScalar(deltaTime * 15.0 * dampingFactor))

        // More friction at the bottom to simulate particles piling up
        const halfHeight = window.innerHeight / 2
        const floorDistance = halfHeight - this.pos.y
        if (floorDistance < particleSize * 3) {
          // Stronger friction near the floor
          this.vel.multiplyScalar(0.92)
          this.vel.x *= 0.96 // More horizontal damping to reduce sliding
          this.rotationVel *= 0.95
        } else {
          // Normal air friction
          this.vel.multiplyScalar(0.985)
          this.rotationVel *= 0.99
        }

        // Update position
        this.pos.add(this.vel.clone().multiplyScalar(deltaTime * 11.5))

        // Boundary collisions
        const bounce = 0.45
        const buffer = particleSize * 2 // Adjusted for cloud size
        const halfWidth = window.innerWidth / 2

        // Side walls
        if (this.pos.x < -halfWidth + buffer) {
          this.pos.x = -halfWidth + buffer
          this.vel.x = Math.abs(this.vel.x) * bounce
        }

        if (this.pos.x > halfWidth - buffer) {
          this.pos.x = halfWidth - buffer
          this.vel.x = -Math.abs(this.vel.x) * bounce
        }

        // Floor and ceiling
        if (this.pos.y < -halfHeight + buffer) {
          this.pos.y = -halfHeight + buffer
          this.vel.y = Math.abs(this.vel.y) * bounce
        }

        if (this.pos.y > halfHeight - buffer) {
          this.pos.y = halfHeight - buffer
          this.vel.y = -Math.abs(this.vel.y) * 0.6 // Higher energy loss on floor
        }

        // Reset acceleration after update
        this.acc.set(0, 0, 0)
        this.densityFactor = 0
      }

      draw() {
        // Interpolate position for smooth rendering
        const renderX = THREE.MathUtils.lerp(this.lastPos.x, this.pos.x, 0.5)
        const renderY = THREE.MathUtils.lerp(this.lastPos.y, this.pos.y, 0.5)

        this.mesh.position.set(renderX, renderY, 0)
        this.mesh.rotation.z = this.rotation
      }

      interact(other: Particle) {
        const d = this.pos.distanceTo(other.pos)

        if (d < spacing) {
          const densityIncrease = THREE.MathUtils.mapLinear(d, 0, spacing, 1.2, 0.1)
          this.densityFactor += densityIncrease
          other.densityFactor += densityIncrease

          const force = new THREE.Vector3().subVectors(this.pos, other.pos).normalize()

          let strength = THREE.MathUtils.mapLinear(d, 0, spacing, 0.8, 0)
          strength = Math.pow(strength, 1.1)
          force.multiplyScalar(strength)

          const overlap = spacing - d
          if (overlap > 0) {
            const correctionStrength = THREE.MathUtils.mapLinear(overlap, 0, spacing, 0.15, 0.25)
            const correction = force.clone().multiplyScalar(overlap * correctionStrength)

            // Increase boundary factor near the floor to prevent particles from stacking too high
            let boundaryFactor = 1.0
            const halfHeight = window.innerHeight / 2
            const floorDistance = halfHeight - this.pos.y

            if (floorDistance < particleSize * 4 || halfHeight - other.pos.y < particleSize * 4) {
              boundaryFactor = 0.7
            }

            correction.multiplyScalar(boundaryFactor)

            const densityScale = THREE.MathUtils.mapLinear(this.densityFactor + other.densityFactor, 0, 10, 1, 0.9)

            const correctionWeight = 0.15 * densityScale
            this.pos.add(correction.clone().multiplyScalar(correctionWeight))
            other.pos.sub(correction.clone().multiplyScalar(correctionWeight))

            const avgVel = new THREE.Vector3().addVectors(this.vel, other.vel).multiplyScalar(0.5)
            let velocityBlend = THREE.MathUtils.mapLinear(d, 0, spacing, 0.15, 0.02)
            velocityBlend *= THREE.MathUtils.mapLinear(this.densityFactor + other.densityFactor, 0, 10, 1.2, 0.95)

            if (d < spacing * 0.5) {
              velocityBlend *= 1.5
            }

            this.vel.lerp(avgVel, velocityBlend)
            other.vel.lerp(avgVel, velocityBlend)
          }

          const accForce = force.clone().multiplyScalar(0.4)
          this.acc.add(accForce)
          other.acc.sub(accForce)
        }
      }

      dispose() {
        scene.remove(this.mesh)
        this.mesh.geometry.dispose()
        this.mesh.material.dispose()
      }
    }

    // Create particles - starting from the top of the screen
    const particles: Particle[] = []
    const availableWidth = window.innerWidth * 0.95
    const cols = Math.floor(availableWidth / spacing)
    const startX: number = (window.innerWidth - cols * spacing) * 0.5 - window.innerWidth / 2
    const startY: number = -window.innerHeight / 2 + window.innerHeight * 0.1

    let count = 0
    let row = 0

    while (count < particleCount) {
      for (let col = 0; col < cols && count < particleCount; col++) {
        const x = startX + col * spacing + (Math.random() * 10 - 2)
        const y = startY + row * spacing * 0.5 + (Math.random() * 10 - 2)
        particles.push(new Particle(x, y))
        count++
      }
      row++
    }

    // Grid-based optimization for interactions
    function updateParticles() {
      const gridSize = spacing
      const grid = {}

      // Update particles and assign to grid
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]
        p.update()

        const gridX = Math.floor(p.pos.x / gridSize)
        const gridY = Math.floor(p.pos.y / gridSize)
        const key = `${gridX},${gridY}`

        if (!grid[key]) {
          grid[key] = []
        }

        grid[key].push(i)
      }

      // Process interactions
      for (const key in grid) {
        const cell = grid[key]
        const [gx, gy] = key.split(",").map(Number)

        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            const neighborKey = `${gx + dx},${gy + dy}`
            if (grid[neighborKey]) {
              for (const i of cell) {
                for (const j of grid[neighborKey]) {
                  if (i < j) {
                    particles[i].interact(particles[j])
                  }
                }
              }
            }
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        p.draw()
      }
    }

    // Animation loop
    let animationFrameId: number
    let prevTime = 0

    function animate(time: number) {
      animationFrameId = requestAnimationFrame(animate)
      deltaTime = Math.min(1 / 30, deltaTime * 0.1 + 0.9 / Math.max(60, 1000 / (time - prevTime || 16)))
      prevTime = time
      updateParticles()
      mousePrevX = mouseX
      mousePrevY = mouseY
      renderer.render(scene, camera)
    }

    animate(0)

    // Event listeners
    const handleResize = () => {
      camera.left = window.innerWidth / -2
      camera.right = window.innerWidth / 2
      camera.top = window.innerHeight / 2
      camera.bottom = window.innerHeight / -2
      camera.updateProjectionMatrix()
      renderer.setSize(window.innerWidth, window.innerHeight)
    }

    function handleMouseMove(event: MouseEvent) {
      // Convert mouse coordinates to scene coordinates
      const rect = renderer.domElement.getBoundingClientRect()
      mouseX = event.clientX - rect.left - rect.width / 2
      mouseY = -(event.clientY - rect.top - rect.height / 2)
    }

    const handleMouseDown = () => {
      mouseIsPressed = true
    }

    const handleMouseUp = () => {
      mouseIsPressed = false
    }

    window.addEventListener("resize", handleResize)
    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("mousedown", handleMouseDown)
    window.addEventListener("mouseup", handleMouseUp)

    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId)
      particles.forEach(p => p.dispose())
      window.removeEventListener("resize", handleResize)
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("mousedown", handleMouseDown)
      window.removeEventListener("mouseup", handleMouseUp)
      if (mount && renderer.domElement) {
        mount.removeChild(renderer.domElement)
      }
    }
  }, [pathname, isMobile])

  return <div ref={mountRef} style={{ width: "100%", height: "100vh" }} />
}
